import { Page, PagesPermissions } from "../types/Authorization";
import { AiAreaKey, AiCapabilityMap, AiDraft } from "../types/Ai";
import { isPermissionGranted } from "./navPermissions";
import { startPriceList } from "./priceListHandoff";
import { priceSheetsFor } from "./priceSheets";
import { REPORT_WORDS, findReports } from "./reportFinder";

/**
 * "Do" rows for search (roadmap #31): things a person can start straight from
 * the search box. Matched by CODE from the typed words — no AI, no message
 * used — and offered only when the person's role allows that action (the same
 * create rights the forms and routes check). Ask can still fill the form from
 * the words (one message); opening it empty is free.
 */
export interface AskAction {
    key: string;
    label: string;
    /** Tooltip: what opens. */
    hint: string;
    /** Bootstrap icon name. */
    icon: string;
    /** The Ask area it belongs to, for ordering by the person's main work. */
    area: AiAreaKey;
    /** The words that mean this action. */
    words: RegExp;
    /** The "Page:action" right that allows it; null = everyone (own self-service). */
    permission: string | null;
    /** A check of its own, when one right isn't enough (e.g. edit AND finance). */
    allowed?: (pages: Page[]) => boolean;
    /** Ask can fill this form in from the typed words (uses one message). */
    fillable: boolean;
    /** A create page to go to … */
    to?: string;
    /** … or a form opened in place, empty … */
    form?: AiDraft;
    /** … or something else that opens in place. */
    start?: () => void;
    /** The row's second line, when it isn't a form ("Opens the report · no message used"). */
    note?: string;
}

export const ASK_ACTIONS: AskAction[] = [
    {
        key: "task", label: "New task", hint: "Open a new task form", icon: "check2-square", area: "tasks",
        words: /\b(tasks?|to-?dos?)\b|مهمة/i, permission: PagesPermissions.TASKS_CREATE, fillable: true,
        form: { kind: "task", title: "New task", values: {}, warnings: [] },
    },
    {
        key: "reminder", label: "Remind me", hint: "Open a task for yourself with a due date", icon: "alarm", area: "tasks",
        words: /\bremind(er|ers)?\b|ذكرني/i, permission: PagesPermissions.TASKS_CREATE, fillable: true,
        form: { kind: "task", title: "Reminder", values: { name: "Reminder: " }, warnings: [] },
    },
    {
        key: "leave", label: "Ask for leave", hint: "Open your leave request form", icon: "calendar2-week", area: "myhr",
        words: /\b(leave|day off|days off|vacation|time off|sick)\b|إجازة/i, permission: null, fillable: true,
        form: { kind: "leave", title: "Ask for leave", values: {}, warnings: [] },
    },
    {
        key: "pricelist", label: "Fill a price sheet with AI", hint: "Read a price list (Excel, photo, PDF or pasted) into a rate sheet, quote or mission", icon: "stars", area: "crm",
        words: /\b(price|rate)\s*(lists?|sheets?|cards?)\b|\btariffs?\b|قائمة أسعار/i, permission: null, fillable: false,
        allowed: (pages) => priceSheetsFor(pages).length > 0, start: () => startPriceList(),
    },
    {
        key: "request", label: "New mission request", hint: "Open the new mission request page", icon: "clipboard-plus", area: "requests",
        words: /\b(mission requests?|quotes?|quotations?|rfqs?)\b|\b(?<!leave )(?<!hr )requests?\b/i, permission: PagesPermissions.MISSION_REQUESTS_CREATE, fillable: true,
        to: "/mission-request",
    },
    {
        key: "mission", label: "New mission", hint: "Open the new mission page", icon: "geo-alt", area: "missions",
        words: /\bmissions?\b(?!\s+requests?)/i, permission: PagesPermissions.MISSIONS_CREATE, fillable: false,
        to: "/mission",
    },
    {
        key: "lead", label: "New lead", hint: "Open the new lead page", icon: "person-plus", area: "crm",
        words: /\bleads?\b/i, permission: PagesPermissions.LEADS_CREATE, fillable: false,
        to: "/lead",
    },
    {
        key: "client", label: "New client", hint: "Open the new client page", icon: "building-add", area: "crm",
        words: /\bclients?\b/i, permission: PagesPermissions.CLIENTS_CREATE, fillable: false,
        to: "/client",
    },
    {
        key: "provider", label: "New provider", hint: "Open the new provider page", icon: "truck", area: "crm",
        words: /\b(providers?|suppliers?|vendors?)\b/i, permission: PagesPermissions.PROVIDERS_CREATE, fillable: false,
        to: "/provider",
    },
    {
        key: "contact", label: "New contact", hint: "Open the new contact page", icon: "person-vcard", area: "crm",
        words: /\bcontacts?\b/i, permission: PagesPermissions.CONTACTS_CREATE, fillable: false,
        to: "/contact",
    },
];

/** Words that say the person wants to DO something rather than find something. */
const ACTION_WORDS = /\b(new|create|add|make|open|start|log|record|book|put in|raise|submit|file|upload|import|apply for|take|set up|i want to|i need to|i'd like to|remind me|let me)\b|أريد|اضف|أضف|انشئ|أنشئ/i;

/** The actions this person may do, given their pages. */
export const allowedActions = (pages: Page[]): AskAction[] =>
    ASK_ACTIONS.filter((action) => (action.allowed
        ? action.allowed(pages)
        : action.permission === null || isPermissionGranted(pages, action.permission)));

/**
 * Actions for typed words: allowed ones whose words appear, earliest first
 * (so "a task for client Acme" puts New task before New client), at most 3.
 * `isAction` says the words read as doing (an action word is present), which
 * puts these rows first.
 * @param text - What was typed.
 * @param pages - The person's pages.
 */
export const matchActions = (text: string, pages: Page[]): { actions: AskAction[]; isAction: boolean } => {
    const trimmed = text.trim();
    if (trimmed.length < 3) return { actions: [], isAction: false };
    // "show me …" / "… report": the matching report(s) first, then any forms
    const wantsReport = REPORT_WORDS.test(trimmed);
    const reports = findReports(trimmed, pages, wantsReport ? 2 : 1)
        .filter((match) => wantsReport || match.score >= 4)
        .map((match): AskAction => ({
            key: `report:${match.tab}:${match.report.key}`,
            label: `Open report: ${match.report.label}`,
            hint: match.report.description,
            icon: "bar-chart-line",
            area: "overview",
            words: REPORT_WORDS,
            permission: null,
            fillable: false,
            to: match.to,
            note: `${match.tabLabel} report · no AI credit used`,
        }));
    const forms = wantsReport && reports.length ? [] : allowedActions(pages)
        .map((action) => ({ action, at: trimmed.search(action.words) }))
        .filter((hit) => hit.at >= 0)
        .sort((a, b) => a.at - b.at)
        .map((hit) => hit.action);
    const actions = [...reports, ...forms].slice(0, 3);
    return { actions, isAction: (wantsReport && reports.length > 0) || (forms.length > 0 && ACTION_WORDS.test(trimmed)) };
};

/**
 * The person's top actions for an empty search box, ordered by the areas of
 * their main work (the capabilities ranking), at most `max`.
 */
export const topActions = (pages: Page[], capabilities: AiCapabilityMap | null, max = 4): AskAction[] => {
    const order = (capabilities?.areas ?? []).map((area) => area.key);
    const rank = (action: AskAction) => {
        const at = order.indexOf(action.area);
        return at < 0 ? order.length : at;
    };
    return allowedActions(pages)
        .map((action, index) => ({ action, index }))
        .sort((a, b) => rank(a.action) - rank(b.action) || a.index - b.index)
        .slice(0, max)
        .map((hit) => hit.action);
};

/** "AI · 1 credit · 12 left today" for rows that ask the AI. */
export const messageCost = (access: { fastPerDay?: number | null; usedFast?: number } | null | undefined): string => {
    if (typeof access?.fastPerDay !== "number") return "AI · 1 credit · no daily limit";
    const left = Math.max(0, access.fastPerDay - (access.usedFast ?? 0));
    return left === 0 ? "AI · no credits left today" : `AI · 1 credit · ${left} left today`;
};
